/**
 * The embed builder's pure half — what the EMBED panel in dashboard SETTINGS and its tests share.
 * The script itself is `public/embed/portal-events.js`; these defaults mirror its DEFAULTS, so a
 * snippet only carries what the owner changed.
 */
import { BRAND_URL } from '../../utils/brand';

export const EMBED_DEFAULTS = {
  theme: 'dark', layout: 'list', limit: 6, accent: '#FF00AA', font: 'portal', radius: 24,
  button: 'BUY TICKETS', poster: 'show', brand: 'show', past: false, css: '',
};

/** SIMPLE shows these; ADVANCED shows everything. */
export const SIMPLE_KEYS = ['theme', 'layout', 'limit'];

export const SCRIPT_URL = `${BRAND_URL}/embed/portal-events.js`;

const attr = (v) => String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');

/** The data attributes a snippet carries — only what differs from the script's defaults. */
export function embedAttrs(opts) {
  const o = { ...EMBED_DEFAULTS, ...(opts || {}) };
  const out = [];
  if (o.theme !== EMBED_DEFAULTS.theme) out.push(['data-theme', o.theme]);
  if (o.layout !== EMBED_DEFAULTS.layout) out.push(['data-layout', o.layout]);
  if (Number(o.limit) !== EMBED_DEFAULTS.limit) out.push(['data-limit', Number(o.limit)]);
  if ((o.accent || '').toUpperCase() !== EMBED_DEFAULTS.accent) out.push(['data-accent', o.accent]);
  if (o.font !== EMBED_DEFAULTS.font) out.push(['data-font', o.font]);
  if (Number(o.radius) !== EMBED_DEFAULTS.radius) out.push(['data-radius', Number(o.radius)]);
  if ((o.button || '').trim() && o.button.trim() !== EMBED_DEFAULTS.button) out.push(['data-button', o.button.trim()]);
  if (o.poster !== EMBED_DEFAULTS.poster) out.push(['data-poster', o.poster]);
  if (o.brand !== EMBED_DEFAULTS.brand) out.push(['data-brand', o.brand]);
  if (o.past) out.push(['data-past', 'true']);
  return out;
}

/** The code the owner pastes: the div (with its options), the script, and their CSS when they wrote some. */
export function embedSnippet(slug, opts) {
  const o = { ...EMBED_DEFAULTS, ...(opts || {}) };
  const attrs = embedAttrs(o).map(([k, v]) => ` ${k}="${attr(v)}"`).join('');
  const css = (o.css || '').trim();
  const lines = [
    '<!-- Portal events: paste this where the list should appear -->',
    `<div data-portal-events="${attr(slug)}"${attrs}></div>`,
    `<script async src="${SCRIPT_URL}"></script>`,
  ];
  if (css) lines.push('<style>', css, '</style>');
  return lines.join('\n');
}

/** What the live preview hands the script: the same options, plus the page and the API it should call. */
export function embedRenderOpts(slug, opts, api) {
  const o = { ...EMBED_DEFAULTS, ...(opts || {}) };
  return { ...o, page: slug, limit: Number(o.limit), radius: Number(o.radius), api };
}

/** A hex colour the script will honour; anything else keeps the last good one. */
export const isHex = (v) => /^#[0-9a-fA-F]{6}$/.test(String(v || '').trim());

/** The hooks a site's own CSS can grab — printed under the custom CSS box. */
export const CSS_HOOKS = [
  ['--pe-bg', 'the box behind everything'],
  ['--pe-card', 'each event card'],
  ['--pe-ink', 'titles and prices'],
  ['--pe-sub', 'venue and city'],
  ['--pe-accent', 'the date line and the button on hover'],
  ['--pe-button / --pe-button-ink', 'the BUY TICKETS pill'],
  ['--pe-radius', 'corner roundness'],
  ['--pe-font', 'the typeface'],
  ['.pe-card · .pe-title · .pe-when · .pe-where · .pe-price · .pe-buy · .pe-poster', 'the pieces, for anything deeper'],
];

export const CSS_EXAMPLE = `/* Your styles win over ours. A few ideas: */
.portal-embed { --pe-accent: #00FFFF; --pe-radius: 8px; }
.portal-embed .pe-title { font-size: 18px; }
.portal-embed .pe-buy { background: #00FFFF; color: #000; }`;
